import * as React from 'react';

import { enumUnit, type Unit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  getUnitSymbol,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Switch,
  Tab,
  Widget,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCamera,
  LucideCheck,
  LucideCheckCircle,
  LucideChevronDown,
  LucideEye,
  LucideEyeOff,
  LucideLoader2,
} from 'lucide-react';

import { $focusedRowId } from '~/modules/trade/_state/loadStaged.state';
import {
  useScaleReading,
  useTakeWeightEmitter,
} from '~/modules/trade/hooks/useScaleReading';
import { useScales } from '~/modules/trade/hooks/useScales';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const formatWeight = (weight: number | null): string => {
  if (weight === null) return '0';
  return weight.toLocaleString(getLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const PHOTO_CAPTURE_SUCCESS_TIMEOUT_MS = 2000;

type ScaleAndCameraWidgetProps = {
  onCapturePhoto?: (lineItemId: string, previewUrl: string) => void;
};

export const ScaleAndCameraWidget: React.FC<ScaleAndCameraWidgetProps> = ({
  onCapturePhoto,
}) => {
  const t = useTranslations();

  const {
    allScales,
    favoriteScales,
    displayedScales,
    currentScale,
    setCurrentScaleId,
    addScaleToFavorite,
    removeScaleFromFavorite,
    canAddScaleToFavorite,
    maxFavoriteScales,
    showScaleCamera,
    setShowScaleCamera,
  } = useScales();

  const focusedRowId = useAtomValue($focusedRowId);

  const hasLineItemFocused = focusedRowId !== null;

  const [photoCaptureSucceeded, setPhotoCaptureSucceeded] =
    React.useState(false);

  const cameraImgRef = React.useRef<HTMLImageElement>(null);

  const { weight, isStable } = useScaleReading(currentScale?.id);

  const resolvedUnit =
    (currentScale?.unitOfMeasurement as Unit) ?? enumUnit.Pounds;
  const { emitTakeWeightEvent } = useTakeWeightEmitter();

  const handleTakeWeightPress = React.useCallback(() => {
    if (weight !== null) {
      emitTakeWeightEvent({
        isStable,
        weight,
        unitOfMeasurement: resolvedUnit,
      });
    }
  }, [emitTakeWeightEvent, isStable, weight, resolvedUnit]);

  const handlePhotoCapture = React.useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      if (!focusedRowId) return;

      const imageElement = cameraImgRef.current;
      if (!imageElement) return;

      const canvas = document.createElement('canvas');
      canvas.width = imageElement.naturalWidth;
      canvas.height = imageElement.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(imageElement, 0, 0);
      const previewUrl = canvas.toDataURL('image/jpeg');

      setPhotoCaptureSucceeded(true);
      onCapturePhoto?.(focusedRowId, previewUrl);

      setTimeout(() => {
        setPhotoCaptureSucceeded(false);
      }, PHOTO_CAPTURE_SUCCESS_TIMEOUT_MS);
    },
    [focusedRowId, onCapturePhoto],
  );

  if (!allScales?.length) return null;

  return (
    <Widget className={cn('@ScaleAndCameraWidget', 'p-0')}>
      <div className="flex justify-between border-b border-neutral/20 px-1.5">
        <div className="flex w-full items-center justify-start gap-1 overflow-hidden">
          {displayedScales?.map((scaleItem) => (
            <div
              key={scaleItem.id}
              title={scaleItem.name}
              className="flex min-w-7 items-center justify-center"
            >
              <Tab
                selected={currentScale?.id === scaleItem.id}
                onClick={() => setCurrentScaleId(scaleItem.id)}
                className="px-1 py-1.5"
                variant="primary"
              >
                {scaleItem.shortName}
              </Tab>
            </div>
          ))}
        </div>

        {(currentScale?.camera || allScales.length > maxFavoriteScales) && (
          <PopoverTrigger>
            <Button
              type="button"
              className="max-h-8.5 rounded-none border-none p-1 outline-none"
              variant="outline"
              aria-label="Scale cameras"
              icon={<LucideChevronDown className="size-4" />}
            />

            <Popover className="mr-4">
              <PopoverDialog>
                <div className="flex min-w-[200px] flex-col p-0 pb-2">
                  {currentScale?.camera && (
                    <div className="flex cursor-pointer items-center justify-between gap-2 bg-neutral/20 p-4 select-none">
                      <Switch
                        onChange={() => setShowScaleCamera(!showScaleCamera)}
                        className="flex w-full cursor-pointer items-center gap-2"
                        isSelected={showScaleCamera}
                      >
                        {t.loadStaged?.showCamera}
                      </Switch>
                    </div>
                  )}

                  {allScales.length > maxFavoriteScales && (
                    <div className="px-4 py-3 font-medium text-neutral/40">
                      {t.loadStaged?.visibleScales ?? 'Visible scales'} ・{' '}
                      <span className="inline-block w-[80px] font-normal">
                        {favoriteScales.length} / {maxFavoriteScales}{' '}
                        {maxFavoriteScales === favoriteScales.length && 'max'}
                      </span>
                    </div>
                  )}

                  {allScales.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-neutral/10"
                    >
                      <button
                        type="button"
                        className="flex-1 cursor-pointer text-left"
                        onClick={() => {
                          setCurrentScaleId(it.id);
                        }}
                      >
                        <div className="font-medium text-neutral">
                          {it.shortName}
                        </div>
                        <div className="text-sm text-neutral/60">{it.name}</div>
                      </button>

                      {allScales.length > maxFavoriteScales && (
                        <button
                          type="button"
                          className={cn(
                            'flex cursor-pointer items-center justify-center rounded-md p-1.5 transition-colors outline-none',
                            it.isFavorite
                              ? 'bg-primary'
                              : 'bg-transparent hover:bg-primary/40',
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (it.isFavorite) {
                              removeScaleFromFavorite(it.id);
                            } else if (canAddScaleToFavorite) {
                              addScaleToFavorite(it.id);
                            }
                          }}
                        >
                          {it.isFavorite ? (
                            <LucideEye size={20} className="text-white" />
                          ) : (
                            <LucideEyeOff
                              size={20}
                              className="text-neutral/40"
                            />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </PopoverDialog>
            </Popover>
          </PopoverTrigger>
        )}
      </div>

      <div className="flex flex-col gap-1.5 p-1.5">
        <div className="flex items-center justify-center gap-3 py-1 transition duration-500">
          <div className="flex gap-1 text-2xl 2xl:text-3xl">
            <span className="text-neutral">{formatWeight(weight)}</span>
            <span className="font-extralight text-neutral/30">
              {getUnitSymbol(currentScale?.unitOfMeasurement)}
            </span>
          </div>

          <div
            className={cn(
              'flex items-center gap-1 text-sm transition',
              isStable ? 'text-success' : 'text-neutral/30',
            )}
          >
            {isStable ? (
              <LucideCheckCircle size={20} />
            ) : (
              <LucideLoader2 size={20} className="animate-spin" />
            )}
          </div>
        </div>

        <Button
          variant="solid"
          className="py-2.5"
          onPress={handleTakeWeightPress}
          isDisabled={!isStable || !hasLineItemFocused}
          aria-label={t.loadStaged?.takeWeight}
        >
          {t.loadStaged?.takeWeight}{' '}
          <span className="ml-2 text-[0.625em] text-white/75 uppercase">
            {t.loadStaged?.space}
          </span>
        </Button>

        {showScaleCamera && currentScale?.camera && (
          <div className="relative aspect-video w-full overflow-hidden rounded">
            <img
              ref={cameraImgRef}
              src={currentScale.camera.streamUrl ?? undefined}
              alt={currentScale.camera.name}
              className="h-full w-full object-cover"
            />

            {hasLineItemFocused && (
              <Button
                className="absolute right-2 bottom-2 max-h-7 max-w-7 border border-white p-1.5"
                variant={photoCaptureSucceeded ? 'outline' : 'solid'}
                onPointerDown={handlePhotoCapture}
              >
                {photoCaptureSucceeded ? (
                  <LucideCheck size={12} />
                ) : (
                  <LucideCamera size={12} />
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </Widget>
  );
};
